import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import TopNavBar from "../../layouts/TopNavBar";
import AlertContext from "../../../../context/Alert/AlertContext";
import axiosPrivate from "../../../../hooks/axiosPrivate";
import classes from "./addProductReviewStyle.module.css";
import { errorToast, successToast } from "../../common/Toaster";
import { GrAddCircle, GrSubtractCircle } from "react-icons/gr";
import AuthContext from "../../../../context/Auth/AuthContext";
import LoaderContext from "../../../../context/Loader/LoaderContext";

// Validation rules
const validationRules = {
  product_id: Yup.string().required("Product is required"),
  reviews: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required("Name is required"),
        rating: Yup.string().required("Rating is required"),
        review_title: Yup.string().required("Review title is required"),
        review: Yup.string().required("Review is required"),
      })
    )
    .min(1, "At least one row is required"),
};

const AddProductReview = () => {
  const { setAlertMessage } = useContext(AlertContext);
  const { authPermissions } = useContext(AuthContext);
  const navigate = useNavigate();
  const { setLoaderCheck } = useContext(LoaderContext);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([
    { name: "", rating: "", review_title: "", review: "" },
  ]);

  const { id } = useParams();

  // Get Product List
  useEffect(() => {
    setLoaderCheck(true);
    axiosPrivate
      .get("products")
      .then((response) => {
        setLoaderCheck(false);
        setProducts(response?.data?.data);
      })
      .catch((error) => {
        setLoaderCheck(false);
        errorToast(error.message);
      });
  }, []);

  useEffect(() => {
    if (id) {
      setLoaderCheck(true);
      axiosPrivate
        .get(`product-review/${id}`)
        .then((response) => {
          setLoaderCheck(false);

          // Ensure the entire structure remains intact
          const finalData = response?.data?.data[0];
          formik.setValues(finalData);
        })
        .catch((error) => {
          setLoaderCheck(false);
          errorToast(error.message);
        });
    }
  }, [id]);

  // Formik setup
  const formik = useFormik({
    initialValues: {
      product_id: "",
      reviews: reviews,
    },
    validationSchema: Yup.object(validationRules),
    onSubmit: (values) => {
      const requetPayload = {
        product_id: values.product_id,
        reviews: values.reviews,
      };
      setLoaderCheck(true);
      if (id) {
        axiosPrivate
          .put(`product-review/update/${id}`, requetPayload)
          .then((response) => {
            setLoaderCheck(false);
            successToast("Product review updated successfully");
            setAlertMessage({
              message: "Product review updated successfully",
            });
            navigate(`/admin/product-review`);
          })
          .catch((error) => {
            setLoaderCheck(false);
            if (error.response) {
              if (error.response.data.errors.length === undefined) {
                errorToast(error.response.data.errors.msg);
              } else {
                for (let i = 0; i < error.response.data.errors.length; i++) {
                  errorToast(error.response.data.errors[i].msg);
                }
              }
            }
          });
      } else {
        axiosPrivate
          .post("product-review", requetPayload)
          .then((response) => {
            setLoaderCheck(false);
            setAlertMessage({
              message: "Product review created successfully",
            });
            successToast("Product review created successfully");
            navigate(`/admin/product-review`);
          })
          .catch((error) => {
            setLoaderCheck(false);
            if (error.response) {
              if (error.response.data.errors.length === undefined) {
                errorToast(error.response.data.errors.msg);
              } else {
                for (let i = 0; i < error.response.data.errors.length; i++) {
                  errorToast(error.response.data.errors[i].msg);
                }
              }
            }
          });
      }
    },
  });

  const addRow = () => {
    const newRow = {
      name: "",
      rating: "",
      review_title: "",
    };
    // Directly update Formik's reviews using setFieldValue
    formik.setFieldValue("reviews", [...formik.values.reviews, newRow]);
  };

  const removeRow = (index) => {
    const newReviews = formik.values.reviews.filter((_, i) => i !== index);
    // Directly update Formik's reviews using setFieldValue
    formik.setFieldValue("reviews", newReviews);
  };

  const restrictInput = (e) => {
    const { value, selectionStart } = e.target;
    const key = e.key;
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
    ];

    // Allow control keys
    if (allowedKeys.includes(key)) return;

    // Allow only digits and one decimal point
    if (!/^\d$|^\.$/.test(key)) {
      e.preventDefault();
      return;
    }

    // Prevent multiple decimals
    if (key === "." && value.includes(".")) {
      e.preventDefault();
      return;
    }

    // Prevent leading decimal
    if (key === "." && selectionStart === 0) {
      e.preventDefault();
      return;
    }

    // Simulate the final value after keypress
    const newValue =
      value.slice(0, selectionStart) + key + value.slice(selectionStart);

    // Prevent out-of-range values (greater than 5 or less than 1)
    if (parseFloat(newValue) > 5 || parseFloat(newValue) < 1) {
      e.preventDefault();
      return;
    }

    // Restrict to only one decimal point
    const decimalIndex = newValue.indexOf(".");
    if (decimalIndex !== -1 && newValue.slice(decimalIndex + 1).length > 1) {
      e.preventDefault();
    }
  };

  const reviewsArray =
    typeof formik?.values?.reviews === "string"
      ? JSON.parse(formik?.values?.reviews)
      : formik?.values?.reviews;

  return (
    <>
      <TopNavBar
        links={{
          list: "/admin/product-review",
          add: "/admin/product-review/add",
        }}
      />
      <div className="section-body">
        <div className="container-fluid">
          <div className="tab-content">
            <form onSubmit={formik.handleSubmit}>
              <div
                className="tab-pane fade show active"
                id="product-add"
                role="tabpanel"
              >
                <div className="card">
                  <div className="card-header">
                    <strong>
                      {id ? "Edit Product Review" : "Add Product Review"}
                    </strong>
                  </div>
                  <div className="card-body">
                    <div className="row clearfix">
                      <div className="col-md-5 col-sm-12">
                        <div className="form-group">
                          <label className="form-label">
                            Product <span style={{ color: "red" }}>*</span>
                          </label>
                          <select
                            className="form-control"
                            id="productId"
                            name="product_id"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values?.product_id}
                          >
                            <option>Select Product</option>
                            {products?.map((product, key) => (
                              <option
                                key={key + product?.name}
                                value={product?.id}
                              >
                                {product?.name}
                              </option>
                            ))}
                          </select>
                          {formik.touched?.product_id &&
                          formik.errors?.product_id ? (
                            <div className={classes.invalidDataError}>
                              {formik.errors?.product_id}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Table data */}
                      {Array.isArray(reviewsArray) &&
                        reviewsArray?.map((review, index) => (
                          <div key={index} className="col-md-12 mb-3">
                            <div className="form-row">
                              <div className="col-md-5 col-sm-12">
                                <label className="form-label">
                                  Name <span style={{ color: "red" }}>*</span>
                                </label>

                                <input
                                  id="name"
                                  name={`reviews[${index}].name`}
                                  type="text"
                                  className="form-control"
                                  placeholder="Name"
                                  value={review.name}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                                {formik?.touched.reviews?.[index]?.name &&
                                  formik.errors.reviews?.[index]?.name && (
                                    <div className={classes.invalidDataError}>
                                      {formik.errors.reviews?.[index]?.name}
                                    </div>
                                  )}
                              </div>

                              <div className="col-md-5 col-sm-12">
                                <label className="form-label">
                                  Rating <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                  id="rating"
                                  name={`reviews[${index}].rating`}
                                  type="text" // Use text to have full control over input
                                  className="form-control"
                                  placeholder="Rating"
                                  value={formik.values.reviews?.[index]?.rating}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  onKeyDown={restrictInput}
                                />

                                {formik.touched.reviews?.[index]?.rating &&
                                  formik.errors.reviews?.[index]?.rating && (
                                    <div className={classes.invalidDataError}>
                                      {formik.errors.reviews?.[index]?.rating}
                                    </div>
                                  )}
                              </div>
                              <div className="col-md-5 col-sm-12">
                                <label className="form-label">
                                  Review Title{" "}
                                  <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                  id="reviewTitle"
                                  name={`reviews[${index}].review_title`}
                                  type="text"
                                  className="form-control"
                                  placeholder="Review Title"
                                  value={review?.review_title}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                                {formik.touched.reviews?.[index]
                                  ?.review_title &&
                                  formik.errors.reviews?.[index]
                                    ?.review_title && (
                                    <div className={classes.invalidDataError}>
                                      {
                                        formik.errors.reviews?.[index]
                                          ?.review_title
                                      }
                                    </div>
                                  )}
                              </div>

                              <div className="col-md-5 col-sm-12">
                                <label className="form-label">
                                  Review <span style={{ color: "red" }}>*</span>
                                </label>
                                <input
                                  id="review"
                                  name={`reviews[${index}].review`}
                                  type="text"
                                  className="form-control"
                                  placeholder="Review"
                                  value={review.review}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                />
                                {formik.touched.reviews?.[index]?.review &&
                                  formik.errors.reviews?.[index]?.review && (
                                    <div className={classes.invalidDataError}>
                                      {formik.errors.reviews?.[index]?.review}
                                    </div>
                                  )}
                              </div>

                              <div className={classes.actionBtns}>
                                <label className="form-label">&nbsp;</label>
                                <button
                                  className={`btn ${classes.addBtn}`}
                                  onClick={addRow}
                                >
                                  <GrAddCircle />
                                </button>
                                {formik.values.reviews.length > 1 && (
                                  <button
                                    className={`btn ${classes.removeBtn}`}
                                    onClick={() => removeRow(index)}
                                  >
                                    <GrSubtractCircle />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}

                      <div className="col-12 text-right">
                        <hr className="mt-4" />
                        <button
                          type="button"
                          id="button_1"
                          className="btn btn-secondary mx-1"
                          data-dismiss="modal"
                          onClick={() => navigate("/admin/product-review")}
                        >
                          CLOSE
                        </button>
                        {authPermissions.includes("Product-create") && (
                          <button
                            type="submit"
                            id="button_2"
                            className="btn btn-primary"
                          >
                            SUBMIT
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductReview;
